// Main entry point for Vite

// Shoelace's themes, from the installed package (#398 C4). FIRST of every import, because the
// bundle's CSS follows module evaluation order: './app-index' below brings in global.css, whose
// :root overrides of Shoelace variables (--sl-font-sans) must come after the theme to win at equal
// specificity, as they did when the themes were <link> tags to a CDN ahead of this script.
import './styles/sl-themes.css';

// Global Shoelace control overrides (disabled-field legibility + checkbox
// visibility). It adds a stylesheet to Shoelace's own control classes, and an
// instance adopts its class's styles once, when it is first connected (its
// shadow root created), so it must run before the app shell CONNECTS the first
// control. It does not depend on running before the tags are registered: in the
// built app Shoelace registers them from the vendor chunk first
// (ui/vite.config.ts manualChunks), and the module handles either order. See
// the module for the full rationale.
import './styles/apply-shoelace-overrides';

// Browser autofill off on every sl-input and sl-textarea (issue #380; data-entry-autofill.md).
// It adds a Lit initializer to Shoelace's input and textarea classes, which runs in each
// instance's constructor, so it must be added before the FIRST field is CONSTRUCTED - an earlier
// boundary than the overrides' first connect above - that is, before './app-index' below renders
// the shell. Neither needs to run before the tags are registered: in the built app Shoelace
// registers them from the vendor chunk before any module here runs (ui/vite.config.ts
// manualChunks), which is also why the autofill default is not a registered subclass.
import './shoelace/data-entry-autofill';

// A focused number field steps its value when the wheel turns over it, so scrolling a form silently
// edits it: a width typed as 3500 was stored as 3499.3. Installed here for the same reason as the
// Shoelace overrides above - one listener at the document reaches every control, including the ones
// Shoelace keeps in a shadow root, and covers fields nobody has written yet.
import { installNumberInputWheelGuard } from './utility/number-input-wheel-guard';

installNumberInputWheelGuard();

// Every dialog and drawer opens with the cursor in its first field rather than on its panel (#380
// U25). One document listener for Shoelace's cancelable sl-initial-focus, for the same reason as the
// wheel guard: it reaches dialogs inside any shadow root and covers dialogs nobody has written yet.
// A dialog already open on its first render emits no such event; the module's initializer on
// Shoelace's dialog and drawer classes covers those, added when the import below is evaluated.
import { installDialogInitialFocus } from './utility/dialog-initial-focus';

installDialogInitialFocus();

import './app-index';

// Import styles if needed
import './styles/global.css';
import './styles/tokens.css';
import './styles/shell.css';
// Bootstrap grid provided by CDN in index.html (diagnostic); local import disabled.
// import './global-styles/bootstrap-full.scss';

// Service worker registration (production only; dev SW disabled in VitePWA config).
// Uses virtual:pwa-register so that when a new build is deployed, the page is
// notified via onNeedRefresh and shows an in-app "Update available" prompt —
// the user must reload to continue (online-only app; forced update),
// avoiding any loss of unsaved work.
if (!import.meta.env.DEV) {
  // Lazy-load so dev builds never pull in the PWA client shim.
  void Promise.all([
    import('virtual:pwa-register'),
    import('./components/feedback/update-notifier.js'),
    import('./services/reload-after-update.js'),
    import('./services/new-version-gate.js'),
    import('./utility/notifications/sse-client.js'),
  ]).then(
    ([
      { registerSW },
      { UpdateNotifier },
      { reloadAfterUpdate },
      { openNewVersionGate },
      { SSEClient },
    ]) => {
      const updateSW = registerSW({
        immediate: true,
        onNeedRefresh() {
          // The live-update stream stops as the gate appears, so this tab no longer holds the
          // old service worker in place for any other tab (new-version-gate.ts, @APP-UPDATE-001).
          openNewVersionGate({
            stopLiveUpdates: () => SSEClient.getInstance().stopForGood(),
            showGate: (onReload) => UpdateNotifier.show(onReload),
            onReload: () =>
              // The reload is bounded, never conditional on controllerchange: that event does
              // not arrive when another tab already applied the update, and workbox-window's
              // own 'controlling' path only reloads when a controller existed at page load.
              // Why the reload is safe under either worker: see reload-after-update.ts.
              reloadAfterUpdate({
                apply: () => updateSW(true),
                reload: () => window.location.reload(),
                serviceWorker: navigator.serviceWorker,
                maxWaitMs: 2000,
              }),
          });
        },
        onRegisterError(error) {
          console.error('Service worker registration failed:', error);
        },
      });
    }
  );
}
